import React, { useState, useMemo, useEffect } from "react";
import { Table, TableBody, TableCell, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { useIsMobile } from "@/hooks/use-mobile";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useContractsTable } from "./table/useContractsTable";
import { TableActions } from "./table/TableActions";
import { TableHeader } from "./table/TableHeader";
import { ContractTableHeader } from "./table/ContractTableHeader";
import { ContractTableRow } from "./table/TableRow";
import { MobileContractCard } from "./table/MobileContractCard";
import { importanceBadges } from "./table/types";
import { ColumnOption } from "./ColumnSelector";
import { ContractCardView } from "./table/ContractCardView";
import { ContractListView } from "./table/ContractListView";
import { ViewType } from "./ViewSwitcher";
import { useContractGroups } from "@/hooks/useContractGroups";
import { GroupHeaderRow } from "./table/GroupHeaderRow";
import { ContractGroup } from "@/lib/types";
import { 
  Pagination, 
  PaginationContent, 
  PaginationItem, 
  PaginationLink, 
  PaginationNext, 
  PaginationPrevious,
  PaginationEllipsis
} from "@/components/ui/pagination";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import { ChevronLeft, ChevronRight, ChevronDown, Users } from "lucide-react";
import { useUserPreferences } from "@/hooks/useUserPreferences";
import { useCurrencies } from "@/hooks/useCurrencies";
import { currencyConverter } from "@/services/currencyConverter";
import { useSystemSettings } from "@/hooks/useSystemSettings";
import { TranslationInput } from "@/components/i18n/TranslationInput";
import { TranslationText } from "../i18n/TranslationText";
import { useTranslation } from "react-i18next";

interface ContractsTableProps {
  availableColumns: ColumnOption[];
  selectedColumns: string[];
  onColumnChange: (columns: string[]) => void;
  viewType: ViewType;
  onViewChange?: (view: ViewType) => void;
  isAdmin?: boolean;
  onUpdateAllRates?: () => Promise<void>;
  isUpdatingRates?: boolean;
  importanceBadges?: any;
  canUseGroupedContracts?: boolean;
}

export function ContractsTable({ 
  availableColumns, 
  selectedColumns: selectedColumnsFromProps, 
  onColumnChange,
  viewType,
  onViewChange,
  isAdmin,
  onUpdateAllRates,
  isUpdatingRates,
  importanceBadges,
  canUseGroupedContracts
}: ContractsTableProps) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const { useUserPreferencesQuery } = useUserPreferences();
  const { useCurrencyQuery } = useCurrencies();
  const [convertedTotalCost, setConvertedTotalCost] = useState<number | null>(null);
  const [convertedTotalIncome, setConvertedTotalIncome] = useState<number | null>(null);
  const [isConvertingTotal, setIsConvertingTotal] = useState(false);
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
  
  const [groupTotals, setGroupTotals] = useState<Record<string, { 
    total: number, 
    convertedCost: number | null,
    convertedIncome: number | null,
    isConverting: boolean 
  }>>({});
  
  const { useAssignContractsToGroupMutation, useRemoveContractsFromGroupMutation } = useContractGroups();
  const assignContractsToGroupMutation = useAssignContractsToGroupMutation();
  const removeContractsFromGroupMutation = useRemoveContractsFromGroupMutation();
  
  const { data: userPreferences } = useUserPreferencesQuery();
  const { 
    data: defaultCurrency,
    isLoading: isDefaultCurrencyLoading
  } = useCurrencyQuery(userPreferences?.defaultCurrencyId || '');
  
  const showGroupedContracts = userPreferences?.showGroupedContracts !== false;
  
  const { getPublicBooleanSetting } = useSystemSettings();
  const [isCurrencyConversionEnabled, setIsCurrencyConversionEnabled] = useState(true);
  
  useEffect(() => {
    const checkFeatures = async () => {
      try {
        const currencyConversionEnabled = await getPublicBooleanSetting("enable_currency_conversion", true);
        console.log("ContractsTable: Currency conversion feature enabled:", currencyConversionEnabled);
        setIsCurrencyConversionEnabled(currencyConversionEnabled);
      } catch (error) {
        console.error("Error checking feature settings:", error);
        setIsCurrencyConversionEnabled(true);
      }
    };
    
    checkFeatures();
  }, [getPublicBooleanSetting]);
  
  const { 
    searchTerm,
    setSearchTerm,
    selectedContracts,
    setSelectedContracts,
    sortColumn,
    sortDirection,
    filters,
    isLoading,
    isError,
    uniqueTypes,
    uniqueCategories,
    contracts,
    filteredContracts,
    sortedContracts,
    paginatedContracts,
    currentPage,
    pageSize,
    totalPages,
    handlePageSizeChange,
    goToPage,
    goToNextPage,
    goToPreviousPage,
    toggleSort,
    resetFilters,
    toggleFilter,
    toggleContractSelection,
    toggleAllSelection,
    handleDeleteContract,
    handleBulkDelete,
    handleContractTouchStart,
    handleContractTouchEnd,
    handleContractTouchMove,
    handleContractTap,
    isColumnSelected,
    hasActiveFilters,
    contractGroups
  } = useContractsTable(selectedColumnsFromProps);

  const convertedFilters = useMemo(() => {
    const result: Record<string, Set<string>> = {};
    
    if (filters.importance && filters.importance.length > 0) {
      result.importance = new Set(filters.importance.map(val => val.toString()));
    }
    
    if (filters.type && filters.type.length > 0) {
      result.type = new Set(filters.type);
    }
    
    if (filters.category && filters.category.length > 0) {
      result.category = new Set(filters.category);
    }
    
    if (filters.expiryStatus && filters.expiryStatus.length > 0) {
      result.expiryStatus = new Set(filters.expiryStatus);
    }
    
    if (filters.groups && filters.groups.length > 0) {
      result.groups = new Set(filters.groups);
    }
    
    return result;
  }, [filters]);

  const totalExpectedCost = useMemo(() => {
    return sortedContracts.reduce((acc, contract) => {
      return acc + (Number(contract.expectedCost) || 0);
    }, 0);
  }, [sortedContracts]);

  const mainCurrency = useMemo(() => {
    return sortedContracts.length > 0 && sortedContracts[0].currency 
      ? sortedContracts[0].currency.symbol 
      : '';
  }, [sortedContracts]);

  const groupedContracts = useMemo(() => {
    const result: { group: ContractGroup | null; contracts: any[] }[] = [
      { group: null, contracts: [] }
    ];
    
    if (!showGroupedContracts) {
      result[0].contracts = paginatedContracts;
      return result;
    }
    
    const groupMap = new Map<string, ContractGroup>();
    contractGroups.forEach(group => {
      groupMap.set(group.id, group);
      result.push({ group, contracts: [] });
    });
    
    paginatedContracts.forEach(contract => {
      if (contract.groupId && groupMap.has(contract.groupId)) {
        const groupIndex = result.findIndex(g => g.group?.id === contract.groupId);
        if (groupIndex !== -1) {
          result[groupIndex].contracts.push(contract);
        } else {
          result[0].contracts.push(contract);
        }
      } else {
        result[0].contracts.push(contract);
      }
    });
    
    return result.filter(group => group.contracts.length > 0);
  }, [paginatedContracts, contractGroups, showGroupedContracts]);
    
  const handleGroupContracts = async (contractIds: string[], groupId: string) => {
    try {
      await assignContractsToGroupMutation.mutateAsync({ contractIds, groupId });
      setSelectedContracts(new Set());
    } catch (error) {
      console.error("Error grouping contracts:", error);
    }
  };

  const handleRemoveFromGroup = async (contractIds: string[]) => {
    try {
      await removeContractsFromGroupMutation.mutateAsync(contractIds);
      setSelectedContracts(new Set());
    } catch (error) {
      console.error("Error removing contracts from group:", error);
    }
  };
  
  useEffect(() => {
    async function convertTotalToDefaultCurrency() {
      if (
        sortedContracts.length > 0 && 
        userPreferences?.defaultCurrencyId && 
        defaultCurrency &&
        sortedContracts.some(contract => contract.currencyId !== userPreferences.defaultCurrencyId)
      ) {
        setIsConvertingTotal(true);
        let convertedCost = 0;
        let convertedIncome = 0;
        
        try {
          for (const contract of sortedContracts) {
            if (!contract.expectedCost) continue;
            
            const contractAmount = Number(contract.expectedCost) || 0;
            let convertedAmount = 0;
            
            if (contract.currencyId === userPreferences.defaultCurrencyId) {
              convertedAmount = contractAmount;
            } else if (contract.currencyId) {
              const result = await currencyConverter.convert(
                contractAmount,
                contract.currencyId,
                userPreferences.defaultCurrencyId
              );
              convertedAmount = result.convertedAmount;
            } else {
              convertedAmount = contractAmount;
            }
            
            if (contract.type?.direction === 'incoming') {
              convertedIncome += convertedAmount;
            } else {
              convertedCost += convertedAmount;
            }
          }
          
          setConvertedTotalCost(convertedCost);
          setConvertedTotalIncome(convertedIncome);
        } catch (error) {
          console.error("Error converting total cost:", error);
          setConvertedTotalCost(null);
          setConvertedTotalIncome(null);
        } finally {
          setIsConvertingTotal(false);
        }
      } else {
        setConvertedTotalCost(null);
        setConvertedTotalIncome(null);
      }
    }
    
    convertTotalToDefaultCurrency();
  }, [sortedContracts, userPreferences?.defaultCurrencyId, defaultCurrency]);

  const totalExpectedIncome = useMemo(() => {
    return sortedContracts.reduce((acc, contract) => {
      return acc + (contract.type?.direction === 'incoming' ? Number(contract.expectedCost) || 0 : 0);
    }, 0);
  }, [sortedContracts]);

  useEffect(() => {
    async function calculateGroupTotals() {
      if (!userPreferences?.defaultCurrencyId || !defaultCurrency || !showGroupedContracts) {
        return;
      }
      
      const newGroupTotals: Record<string, { 
        total: number, 
        convertedCost: number | null,
        convertedIncome: number | null,
        isConverting: boolean 
      }> = {};
      
      for (const group of contractGroups) {
        const groupContracts = sortedContracts.filter(contract => contract.groupId === group.id);
        
        if (groupContracts.length === 0) continue;
        
        const groupTotal = groupContracts.reduce((acc, contract) => {
          return acc + (Number(contract.expectedCost) || 0);
        }, 0);
        
        newGroupTotals[group.id] = {
          total: groupTotal,
          convertedCost: null,
          convertedIncome: null,
          isConverting: true
        };
      }
      
      setGroupTotals(newGroupTotals);
      
      for (const group of contractGroups) {
        const groupContracts = sortedContracts.filter(contract => contract.groupId === group.id);
        
        if (groupContracts.length === 0) continue;
        
        try {
          let convertedCost = 0;
          let convertedIncome = 0;
          
          for (const contract of groupContracts) {
            if (!contract.expectedCost) continue;
            
            let convertedAmount = 0;
            const contractAmount = Number(contract.expectedCost) || 0;
            
            if (contract.currencyId === userPreferences.defaultCurrencyId) {
              convertedAmount = contractAmount;
            } else if (contract.currencyId) {
              const result = await currencyConverter.convert(
                contractAmount,
                contract.currencyId,
                userPreferences.defaultCurrencyId
              );
              convertedAmount = result.convertedAmount;
            } else {
              convertedAmount = contractAmount;
            }
            
            if (contract.type?.direction === 'incoming') {
              convertedIncome += convertedAmount;
            } else {
              convertedCost += convertedAmount;
            }
          }
          
          setGroupTotals(prev => ({
            ...prev,
            [group.id]: {
              ...prev[group.id],
              convertedCost,
              convertedIncome,
              isConverting: false
            }
          }));
        } catch (error) {
          console.error(`Error converting total cost for group ${group.id}:`, error);
          setGroupTotals(prev => ({
            ...prev,
            [group.id]: {
              ...prev[group.id],
              convertedCost: null,
              convertedIncome: null,
              isConverting: false
            }
          }));
        }
      }
    }
    
    calculateGroupTotals();
  }, [sortedContracts, userPreferences?.defaultCurrencyId, defaultCurrency, contractGroups, showGroupedContracts]);

  const toggleGroupExpansion = (groupId: string) => {
    setCollapsedGroups(prevState => {
      const newState = new Set(prevState);
      if (newState.has(groupId)) {
        newState.delete(groupId);
      } else {
        newState.add(groupId);
      }
      return newState;
    });
  };

  const renderPaginationItems = () => {
    const items = [];
    const maxVisiblePages = 5;
    
    if (currentPage > 3) {
      items.push(
        <PaginationItem key="first">
          <PaginationLink onClick={() => goToPage(1)}>
            1
          </PaginationLink>
        </PaginationItem>
      );
      
      if (currentPage > 4) {
        items.push(
          <PaginationItem key="ellipsis-start">
            <PaginationEllipsis />
          </PaginationItem>
        );
      }
    }
    
    let startPage = Math.max(1, currentPage - Math.floor((maxVisiblePages - 1) / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    
    if (endPage - startPage < maxVisiblePages - 1) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }
    
    for (let i = startPage; i <= endPage; i++) {
      items.push(
        <PaginationItem key={i}>
          <PaginationLink 
            isActive={i === currentPage}
            onClick={() => goToPage(i)}
          >
            {i}
          </PaginationLink>
        </PaginationItem>
      );
    }
    
    if (endPage < totalPages - 1) {
      items.push(
        <PaginationItem key="ellipsis-end">
          <PaginationEllipsis />
        </PaginationItem>
      );
    }
    
    if (endPage < totalPages) {
      items.push(
        <PaginationItem key="last">
          <PaginationLink onClick={() => goToPage(totalPages)}>
            {totalPages}
          </PaginationLink>
        </PaginationItem>
      );
    }
    
    return items;
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row justify-between gap-4">
          <div className="flex items-center gap-2">
            <Skeleton className="h-10 w-32" />
          </div>
          <Skeleton className="h-10 w-64" />
        </div>

        <div className="rounded-md border bg-card">
          <div className="p-4">
            <div className="space-y-3">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="p-4 rounded-md bg-destructive/10 text-destructive">
        <p>Error loading contracts. Please try again later.</p>
      </div>
    );
  }

  const renderTotalCost = () => {
    return (
      <div className="flex flex-col justify-end items-end gap-1">
        {isConvertingTotal ? (
          <div className="text-xs text-muted-foreground animate-pulse">
            converting...
          </div>
        ) : (
          <>
            <div className="flex items-center gap-3">
              {convertedTotalCost !== null && convertedTotalCost > 0 && defaultCurrency && (
                <div className="text-right">               
                  <span className="text-muted-foreground">
                    {isCurrencyConversionEnabled                     
                      ? <TranslationText i18nKey="totalCostInCurrency">
                      {t('totalCostInCurrency', {currency: defaultCurrency.code })} 
                    </TranslationText> 
                      : <TranslationText i18nKey="totalCost">
                      {t('totalCost')}
                    </TranslationText> } 
                  </span>
                    <span className="font-medium text-red-600 dark:text-red-400">
                      {(isCurrencyConversionEnabled 
                        ? convertedTotalCost 
                        : totalExpectedCost
                      ).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })},-
                    </span>
                </div>
              )}
              
              {convertedTotalIncome !== null && convertedTotalIncome > 0 && defaultCurrency && (
                <div className="text-right">
                  <span className="text-muted-foreground">
                    {isCurrencyConversionEnabled 
                      ? 
                      <TranslationText i18nKey="totalIncomeInCurrency">
                      {t('totalIncomeInCurrency', {currency: defaultCurrency.code })} 
                    </TranslationText>                       
                      : 
                      <TranslationText i18nKey="totalIncome">
                      {t('totalIncome')}: 
                    </TranslationText>
                    }
                  </span>
                <span className="font-medium text-green-600 dark:text-green-400">
                  {(isCurrencyConversionEnabled 
                    ? convertedTotalIncome 
                    : totalExpectedIncome
                  ).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })},-
                </span>
                </div>
              )}
            </div>
            
            {(convertedTotalIncome !== null || convertedTotalCost !== null) && defaultCurrency && (
              <div className="text-right">
                <span className="text-muted-foreground">
                  {isCurrencyConversionEnabled 
                    ? 
                    <TranslationText i18nKey="resultInCurrency">
                    {t('resultInCurrency', {currency: defaultCurrency.code })} 
                  </TranslationText>                     
                    : 
                    <TranslationText i18nKey="result">
                    {t('result')}
                  </TranslationText>
                  }
                </span>
                <span className={`font-medium ${(isCurrencyConversionEnabled 
                  ? (convertedTotalIncome || 0) - (convertedTotalCost || 0) 
                  : (totalExpectedIncome || 0) - (totalExpectedCost || 0)) >= 0 
                    ? 'text-green-600 dark:text-green-400' 
                    : 'text-red-600 dark:text-red-400'}`}>
                  {(isCurrencyConversionEnabled 
                    ? (convertedTotalIncome || 0) - (convertedTotalCost || 0) 
                    : (totalExpectedIncome || 0) - (totalExpectedCost || 0)
                  ).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })},-
                </span>
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  const isGroupColumnSelected = (id: string) => {
    if (id === "group") return false;
    return isColumnSelected(id);
  };

  const renderContractsView = () => {
    if (isMobile) {
      return (
        <div className="rounded-md overflow-hidden">
          {sortedContracts.length === 0 ? (
            <div className="text-center py-8 text-gray-400 bg-gray-950 border border-gray-800 rounded-md">
              No contracts found.
            </div>
          ) : (
            <>
              <ScrollArea className="max-h-[calc(100vh-220px)]">
                <div className="space-y-2 p-2">
                  {paginatedContracts.map((contract) => (
                    <MobileContractCard
                      key={contract.id}
                      contract={contract}
                      isColumnSelected={isGroupColumnSelected}
                      selectedContracts={selectedContracts}
                      toggleContractSelection={toggleContractSelection}
                      handleDeleteContract={handleDeleteContract}
                      handleContractTap={handleContractTap}
                      handleContractTouchStart={handleContractTouchStart}
                      handleContractTouchEnd={handleContractTouchEnd}
                      handleContractTouchMove={handleContractTouchMove}
                    />
                  ))}
                </div>
                <div className="p-4 text-sm text-gray-400 text-center">
                  <p>Tap a contract to view details.</p>
                  <p>Hold for a second to edit.</p>
                </div>
              </ScrollArea>
              
              <div className="border-t dark:border-gray-800 py-3 px-4">
                {renderTotalCost()}
              </div>
            </>
          )}
        </div>
      );
    }
    
    if (viewType === "card") {
      return (
        <div className="rounded-md border bg-card shadow-sm dark:bg-gray-950 dark:border-gray-800 overflow-hidden p-4">
          {showGroupedContracts ? (
            <div className="space-y-6">
              {groupedContracts.map((groupData, groupIndex) => (
                <div key={groupData.group?.id || 'ungrouped'} className="space-y-4">
                  {groupData.group && (
                    <div 
                      className="flex items-center justify-between cursor-pointer bg-blue-50 dark:bg-blue-950/30 p-3 rounded-md"
                      onClick={() => toggleGroupExpansion(groupData.group.id)}
                    >
                      <div className="flex items-center gap-2">
                        {collapsedGroups.has(groupData.group.id) ? (
                          <ChevronRight className="h-4 w-4 text-muted-foreground" />
                        ) : (
                          <ChevronDown className="h-4 w-4 text-muted-foreground" />
                        )}
                        <Users className="h-4 w-4 text-blue-500 dark:text-blue-400" />
                        <span className="text-blue-700 dark:text-blue-300 font-medium">{groupData.group.name}</span>
                        {groupData.group.description && (
                          <span className="text-sm text-muted-foreground">— {groupData.group.description}</span>
                        )}
                        <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 ml-2">
                          {groupData.contracts.length} {groupData.contracts.length === 1 ? 'contract' : 'contracts'}
                        </span>
                      </div>
                      
                      {groupData.group && groupTotals[groupData.group.id] && (
                        <div className="flex items-center gap-3">
                          {groupTotals[groupData.group.id].total !== undefined && (
                            <div className="flex items-center gap-1 text-sm">
                              <span className="text-muted-foreground">Total:</span>
                              <span className="font-medium">
                                {groupTotals[groupData.group.id].total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })},-
                              </span>
                            </div>
                          )}
                          
                          {groupTotals[groupData.group.id].convertedCost !== null && defaultCurrency?.code && (
                            <div className="flex items-center gap-1 text-sm">
                              {groupTotals[groupData.group.id].isConverting ? (
                                <span className="text-xs text-muted-foreground animate-pulse">
                                  converting...
                                </span>
                              ) : (
                                <>
                                  <span className="text-muted-foreground">In {defaultCurrency.code}:</span>
                                  <span className="font-medium text-green-600 dark:text-green-400">
                                    {groupTotals[groupData.group.id].convertedCost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })},-
                                  </span>
                                </>
                              )}
                            </div>
                          )}
                          
                          {groupTotals[groupData.group.id].convertedIncome !== null && defaultCurrency?.code && (
                            <div className="flex items-center gap-1 text-sm">
                              {groupTotals[groupData.group.id].isConverting ? (
                                <span className="text-xs text-muted-foreground animate-pulse">
                                  converting...
                                </span>
                              ) : (
                                <>
                                  <span className="text-muted-foreground">In {defaultCurrency.code}:</span>
                                  <span className="font-medium text-green-600 dark:text-green-400">
                                    {groupTotals[groupData.group.id].convertedIncome.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })},-
                                  </span>
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  
                  {(!groupData.group || !collapsedGroups.has(groupData.group.id)) && (
                    <ContractCardView 
                      contracts={groupData.contracts} 
                      onDeleteContract={handleDeleteContract}
                      isColumnSelected={isGroupColumnSelected}
                      selectedColumns={selectedColumnsFromProps}
                      defaultCurrencySymbol={defaultCurrency?.code}
                    />
                  )}
                </div>
              ))}
            </div>
          ) : (
            <ContractCardView 
              contracts={paginatedContracts} 
              onDeleteContract={handleDeleteContract}
              isColumnSelected={isGroupColumnSelected}
              selectedColumns={selectedColumnsFromProps}
            />
          )}
          
          {paginatedContracts.length > 0 && (
            <div className="border-t dark:border-gray-800 mt-4 pt-4 text-right">
              {renderTotalCost()}
            </div>
          )}
        </div>
      );
    }
    
    if (viewType === "list") {
      return (
        <div className="rounded-md border bg-card shadow-sm dark:bg-gray-950 dark:border-gray-800 overflow-hidden p-4">
          {showGroupedContracts ? (
            <div className="space-y-6">
              {groupedContracts.map((groupData) => (
                <div key={groupData.group?.id || 'ungrouped'} className="space-y-4">
                  {groupData.group && (
                    <div 
                      className="flex items-center justify-between cursor-pointer bg-blue-50 dark:bg-blue-950/30 p-3 rounded-md"
                      onClick={() => toggleGroupExpansion(groupData.group.id)}
                    >
                      <div className="flex items-center gap-2">
                        {collapsedGroups.has(groupData.group.id) ? (
                          <ChevronRight className="h-4 w-4 text-muted-foreground" />
                        ) : (
                          <ChevronDown className="h-4 w-4 text-muted-foreground" />
                        )}
                        <Users className="h-4 w-4 text-blue-500 dark:text-blue-400" />
                        <span className="text-blue-700 dark:text-blue-300 font-medium">{groupData.group.name}</span>
                        {groupData.group.description && (
                          <span className="text-sm text-muted-foreground">— {groupData.group.description}</span>
                        )}
                        <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 ml-2">
                          {groupData.contracts.length} {groupData.contracts.length === 1 ? 'contract' : 'contracts'}
                        </span>
                      </div>
                      
                      {groupData.group && groupTotals[groupData.group.id] && (
                        <div className="flex items-center gap-3">
                          {groupTotals[groupData.group.id].total !== undefined && (
                            <div className="flex items-center gap-1 text-sm">
                              <span className="text-muted-foreground">Total:</span>
                              <span className="font-medium">
                                {groupTotals[groupData.group.id].total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })},-
                              </span>
                            </div>
                          )}
                          
                          {groupTotals[groupData.group.id].convertedCost !== null && defaultCurrency?.code && (
                            <div className="flex items-center gap-1 text-sm">
                              {groupTotals[groupData.group.id].isConverting ? (
                                <span className="text-xs text-muted-foreground animate-pulse">
                                  converting...
                                </span>
                              ) : (
                                <>
                                  <span className="text-muted-foreground">In {defaultCurrency.code}:</span>
                                  <span className="font-medium text-green-600 dark:text-green-400">
                                    {groupTotals[groupData.group.id].convertedCost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })},-
                                  </span>
                                </>
                              )}
                            </div>
                          )}
                          
                          {groupTotals[groupData.group.id].convertedIncome !== null && defaultCurrency?.code && (
                            <div className="flex items-center gap-1 text-sm">
                              {groupTotals[groupData.group.id].isConverting ? (
                                <span className="text-xs text-muted-foreground animate-pulse">
                                  converting...
                                </span>
                              ) : (
                                <>
                                  <span className="text-muted-foreground">In {defaultCurrency.code}:</span>
                                  <span className="font-medium text-green-600 dark:text-green-400">
                                    {groupTotals[groupData.group.id].convertedIncome.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })},-
                                  </span>
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  
                  {(!groupData.group || !collapsedGroups.has(groupData.group.id)) && (
                    <ContractListView 
                      contracts={groupData.contracts} 
                      onDeleteContract={handleDeleteContract}
                      isColumnSelected={isGroupColumnSelected}
                      selectedColumns={selectedColumnsFromProps}
                      defaultCurrencySymbol={defaultCurrency?.code}
                    />
                  )}
                </div>
              ))}
            </div>
          ) : (
            <ContractListView 
              contracts={paginatedContracts} 
              onDeleteContract={handleDeleteContract}
              isColumnSelected={isGroupColumnSelected}
              selectedColumns={selectedColumnsFromProps}
            />
          )}
          
          {paginatedContracts.length > 0 && (
            <div className="border-t dark:border-gray-800 mt-4 pt-4 text-right">
              {renderTotalCost()}
            </div>
          )}
        </div>
      );
    }
    
    return (
      <div className="rounded-md border bg-card shadow-sm dark:bg-gray-950 dark:border-gray-800 overflow-hidden">
        <div className="max-h-[calc(100vh-320px)] overflow-auto">
          <Table className="border-collapse">
            <ContractTableHeader
              selectedColumns={selectedColumnsFromProps}
              isColumnSelected={isGroupColumnSelected}
              sortColumn={sortColumn}
              sortDirection={sortDirection}
              toggleSort={toggleSort}
              toggleAllSelection={toggleAllSelection}
              toggleFilter={toggleFilter}
              sortedContracts={paginatedContracts}
              selectedContracts={selectedContracts}
              filters={convertedFilters}
              importanceBadges={importanceBadges}
              uniqueTypes={uniqueTypes}
              uniqueCategories={uniqueCategories}
              contractGroups={contractGroups}
              className="sticky top-0 z-10"
            />
            <TableBody>
              {paginatedContracts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={selectedColumnsFromProps.filter(id => isGroupColumnSelected(id)).length} className="h-24 text-center">
                    No contracts found.
                  </TableCell>
                </TableRow>
              ) : (
                showGroupedContracts ? (
                  groupedContracts.map((groupData) => {
                    const isGrouped = groupData.group !== null;
                    const isCollapsed = isGrouped && collapsedGroups.has(groupData.group.id);
                    const groupTotalInfo = isGrouped ? groupTotals[groupData.group.id] : undefined;
                    
                    return (
                      <React.Fragment key={isGrouped ? groupData.group.id : 'ungrouped'}>
                        {isGrouped && (
                          <GroupHeaderRow 
                            group={groupData.group}
                            columns={selectedColumnsFromProps.filter(id => isGroupColumnSelected(id)).length}
                            isExpanded={!isCollapsed}
                            onToggleExpand={() => toggleGroupExpansion(groupData.group.id)}
                            contractCount={groupData.contracts.length}
                            totalCostConverted={groupTotalInfo?.convertedCost || undefined}
                            totalIncomeConverted={groupTotalInfo?.convertedIncome || undefined}
                            defaultCurrencySymbol={defaultCurrency?.code}
                            isConvertingTotal={groupTotalInfo?.isConverting}
                            isCurrencyConversionEnabled={isCurrencyConversionEnabled}
                          />
                        )}
                        
                        {(!isGrouped || !isCollapsed) && (
                          groupData.contracts.map((contract, index) => (
                            <ContractTableRow
                              key={contract.id}
                              contract={contract}
                              index={index}
                              isColumnSelected={isGroupColumnSelected}
                              selectedContracts={selectedContracts}
                              toggleContractSelection={toggleContractSelection}
                              handleDeleteContract={handleDeleteContract}
                              isPartOfGroup={isGrouped}
                            />
                          ))
                        )}
                      </React.Fragment>
                    );
                  })
                ) : (
                  paginatedContracts.map((contract, index) => (
                    <ContractTableRow
                      key={contract.id}
                      contract={contract}
                      index={index}
                      isColumnSelected={isGroupColumnSelected}
                      selectedContracts={selectedContracts}
                      toggleContractSelection={toggleContractSelection}
                      handleDeleteContract={handleDeleteContract}
                    />
                  ))
                )
              )}
            </TableBody>
          </Table>
        </div>

        {paginatedContracts.length > 0 && (
          <div className="p-4 border-t dark:border-gray-800 flex flex-col sm:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-2">

              <TranslationText i18nKey="showingXtoYofZ">
                {t('showingXtoYofZ', { 
                  start: (currentPage - 1) * pageSize + 1, 
                  end: Math.min(currentPage * pageSize, sortedContracts.length), 
                  total: sortedContracts.length 
                })}
              </TranslationText>
              
              <Select value={pageSize.toString()} onValueChange={(value) => handlePageSizeChange(Number(value))}>
                <SelectTrigger className="h-8 w-[70px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="20">20</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {renderTotalCost()}
          </div>
        )}
        
        {totalPages > 1 && (
          <div className="flex justify-center py-4 border-t dark:border-gray-800">
            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious 
                    onClick={goToPreviousPage} 
                    className={currentPage === 1 ? "opacity-50 cursor-not-allowed pointer-events-none" : ""}
                  />
                </PaginationItem>
                
                {renderPaginationItems()}
                
                <PaginationItem>
                  <PaginationNext 
                    onClick={goToNextPage} 
                    className={currentPage === totalPages ? "opacity-50 cursor-not-allowed pointer-events-none" : ""}
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <TableActions
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        selectedContracts={selectedContracts}
        handleBulkDelete={handleBulkDelete}
        resetFilters={resetFilters}
        hasActiveFilters={hasActiveFilters}
        onViewChange={onViewChange}
        viewType={viewType}
        onGroupContracts={handleGroupContracts}
        onRemoveFromGroup={handleRemoveFromGroup}
        contractGroups={contractGroups}
        filters={filters}
        toggleFilter={toggleFilter}
        isAdmin={isAdmin}
        onUpdateAllRates={onUpdateAllRates}
        isUpdatingRates={isUpdatingRates}
        availableColumns={availableColumns}
        selectedColumns={selectedColumnsFromProps}
        onColumnChange={onColumnChange}
        setSelectedContracts={setSelectedContracts}
      />
      {renderContractsView()}
    </div>
  );
}
